import {
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ImageUp, Loader2, Map, PencilRuler, Plus, Save, Trash2, X } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';

type Resource = {
  id: string;
  name: string;
  type: string;
  active: boolean;
  locationId?: string | null;
};

type ApiFloorPlanRoom = {
  id: string;
  resourceId: string;
  label?: string | null;
  shapeType: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string | null;
  notes?: string | null;
  resource?: Resource;
};

type ApiFloorPlan = {
  id: string;
  name: string;
  floorCode: string;
  floorLabel: string;
  imageUrl: string;
  imageStorageKey?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  notes?: string | null;
  active: boolean;
  rooms: ApiFloorPlanRoom[];
};

type EditorRoom = {
  id?: string;
  clientId: string;
  resourceId: string;
  label: string;
  shapeType: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  notes: string;
  resource?: Resource;
};

type FloorPlanDraft = {
  id?: string;
  name: string;
  floorCode: string;
  floorLabel: string;
  imageUrl: string;
  imageStorageKey: string;
  imageWidth: number | null;
  imageHeight: number | null;
  notes: string;
  active: boolean;
  rooms: EditorRoom[];
};

type DrawingState = {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
};

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function isRoomType(type: string) {
  return /room|suite|bay|bed|ward|or|theatre|theater|icu/i.test(type);
}

function createClientId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `room-${Math.random().toString(36).slice(2, 10)}`;
}

function emptyDraft(): FloorPlanDraft {
  return {
    name: '',
    floorCode: '',
    floorLabel: '',
    imageUrl: '',
    imageStorageKey: '',
    imageWidth: null,
    imageHeight: null,
    notes: '',
    active: true,
    rooms: [],
  };
}

function normalizePlan(plan: ApiFloorPlan): FloorPlanDraft {
  return {
    id: plan.id,
    name: plan.name,
    floorCode: plan.floorCode,
    floorLabel: plan.floorLabel,
    imageUrl: plan.imageUrl,
    imageStorageKey: plan.imageStorageKey || '',
    imageWidth: plan.imageWidth ?? null,
    imageHeight: plan.imageHeight ?? null,
    notes: plan.notes || '',
    active: plan.active,
    rooms: plan.rooms.map((room) => ({
      id: room.id,
      clientId: room.id,
      resourceId: room.resourceId,
      label: room.label || '',
      shapeType: room.shapeType || 'rect',
      x: room.x,
      y: room.y,
      width: room.width,
      height: room.height,
      color: room.color || '',
      notes: room.notes || '',
      resource: room.resource,
    })),
  };
}

async function readImageSize(url: string) {
  const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.naturalWidth || 1,
        height: image.naturalHeight || 1,
      });
    };
    image.onerror = () => reject(new Error('Unable to read floor plan image.'));
    image.src = url;
  });
  return dimensions;
}

export default function SettingsRoomManagement() {
  const navigate = useNavigate();
  const { fetchApi, fetchJson } = useApiClient();
  const { tr } = useI18n();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const [resources, setResources] = useState<Resource[]>([]);
  const [plans, setPlans] = useState<ApiFloorPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<FloorPlanDraft | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [drawing, setDrawing] = useState<DrawingState | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [resourceRows, floorPlanRows] = await Promise.all([
          fetchJson<Resource[]>('/resources?includeInactive=true'),
          fetchJson<ApiFloorPlan[]>('/resources/floor-plans?includeInactive=true'),
        ]);
        if (!active) return;
        setResources(resourceRows);
        setPlans(floorPlanRows);
        if (floorPlanRows.length) {
          setSelectedPlanId((current) => current ?? floorPlanRows[0].id);
          setDraft((current) => current ?? normalizePlan(floorPlanRows[0]));
        }
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || tr('Failed to load room management settings.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson, tr]);

  const roomResources = useMemo(
    () => resources.filter((resource) => isRoomType(resource.type)).sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  );

  const selectedRoom = useMemo(
    () => draft?.rooms.find((room) => room.clientId === selectedRoomId) ?? null,
    [draft, selectedRoomId],
  );

  const selectedPlanTitle = draft?.floorLabel || draft?.name || tr('New floor');

  function selectPlan(plan: ApiFloorPlan) {
    setSelectedPlanId(plan.id);
    setDraft(normalizePlan(plan));
    setSelectedRoomId(null);
    setDrawMode(false);
    setDrawing(null);
    setError(null);
    setBanner(null);
  }

  function startNewPlan() {
    setSelectedPlanId('new');
    setDraft(emptyDraft());
    setSelectedRoomId(null);
    setDrawMode(false);
    setDrawing(null);
    setError(null);
    setBanner(null);
  }

  function updateDraft(patch: Partial<FloorPlanDraft>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  function updateRoom(clientId: string, patch: Partial<EditorRoom>) {
    setDraft((current) =>
      current
        ? {
            ...current,
            rooms: current.rooms.map((room) => (room.clientId === clientId ? { ...room, ...patch } : room)),
          }
        : current,
    );
  }

  function deleteRoom(clientId: string) {
    setDraft((current) =>
      current
        ? {
            ...current,
            rooms: current.rooms.filter((room) => room.clientId !== clientId),
          }
        : current,
    );
    if (selectedRoomId === clientId) {
      setSelectedRoomId(null);
    }
  }

  function getRelativePoint(event: ReactMouseEvent<HTMLDivElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp((event.clientX - rect.left) / rect.width),
      y: clamp((event.clientY - rect.top) / rect.height),
    };
  }

  function handleCanvasMouseDown(event: ReactMouseEvent<HTMLDivElement>) {
    if (!drawMode || !draft?.imageUrl) return;
    if (event.target !== event.currentTarget) return;
    const point = getRelativePoint(event);
    if (!point) return;
    setDrawing({
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
    });
  }

  function handleCanvasMouseMove(event: ReactMouseEvent<HTMLDivElement>) {
    if (!drawing) return;
    const point = getRelativePoint(event);
    if (!point) return;
    setDrawing((current) => (current ? { ...current, currentX: point.x, currentY: point.y } : current));
  }

  function handleCanvasMouseUp() {
    if (!drawing || !draft) return;
    const x = Math.min(drawing.startX, drawing.currentX);
    const y = Math.min(drawing.startY, drawing.currentY);
    const width = Math.abs(drawing.currentX - drawing.startX);
    const height = Math.abs(drawing.currentY - drawing.startY);
    setDrawing(null);
    if (width < 0.025 || height < 0.025) return;
    const clientId = createClientId();
    setDraft({
      ...draft,
      rooms: [
        ...draft.rooms,
        {
          clientId,
          resourceId: '',
          label: '',
          shapeType: 'rect',
          x,
          y,
          width,
          height,
          color: '',
          notes: '',
        },
      ],
    });
    setSelectedRoomId(clientId);
  }

  async function handlePlanUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !draft) return;
    setUploading(true);
    setError(null);
    setBanner(null);
    try {
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || 'image/png',
          kind: 'floor-plan',
        }),
      });
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/png' },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`Upload failed (${uploadRes.status})`);
      }
      const dimensions = await readImageSize(presign.url);
      updateDraft({
        imageUrl: presign.url,
        imageStorageKey: presign.key,
        imageWidth: dimensions.width,
        imageHeight: dimensions.height,
      });
      setBanner(tr('Floor plan uploaded. Save the floor to persist it.'));
    } catch (err: any) {
      setError(err?.message || tr('Floor plan upload failed.'));
    } finally {
      event.target.value = '';
      setUploading(false);
    }
  }

  async function savePlan() {
    if (!draft) return;
    const duplicateResources = new Set<string>();
    const seenResourceIds = new Set<string>();
    for (const room of draft.rooms) {
      if (!room.resourceId) {
        setError(tr('Each drawn room must be linked to a schedulable room resource.'));
        return;
      }
      if (seenResourceIds.has(room.resourceId)) {
        duplicateResources.add(room.resourceId);
      }
      seenResourceIds.add(room.resourceId);
    }
    if (duplicateResources.size) {
      setError(tr('A room resource can only appear once on the same floor plan.'));
      return;
    }
    if (!draft.name.trim() || !draft.floorCode.trim() || !draft.floorLabel.trim()) {
      setError(tr('Name, floor code, and floor label are required.'));
      return;
    }
    if (!draft.imageUrl) {
      setError(tr('Upload a floor plan image before saving.'));
      return;
    }

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const payload = {
        name: draft.name.trim(),
        floorCode: draft.floorCode.trim(),
        floorLabel: draft.floorLabel.trim(),
        imageUrl: draft.imageUrl,
        imageStorageKey: draft.imageStorageKey || undefined,
        imageWidth: draft.imageWidth || undefined,
        imageHeight: draft.imageHeight || undefined,
        notes: draft.notes.trim() || undefined,
        active: draft.active,
        rooms: draft.rooms.map((room) => ({
          resourceId: room.resourceId,
          label: room.label.trim() || undefined,
          shapeType: room.shapeType || 'rect',
          x: clamp(room.x),
          y: clamp(room.y),
          width: clamp(room.width, 0.01, 1),
          height: clamp(room.height, 0.01, 1),
          color: room.color.trim() || undefined,
          notes: room.notes.trim() || undefined,
        })),
      };
      const path = draft.id ? `/resources/floor-plans/${draft.id}` : '/resources/floor-plans';
      const response = await fetchApi(path, {
        method: draft.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || tr('Unable to save floor plan.'));
      }
      const saved = (await response.json()) as ApiFloorPlan;
      setPlans((current) => {
        const next = current.some((plan) => plan.id === saved.id) ? current.map((plan) => (plan.id === saved.id ? saved : plan)) : [saved, ...current];
        return next.sort((a, b) => a.floorLabel.localeCompare(b.floorLabel) || a.name.localeCompare(b.name));
      });
      setSelectedPlanId(saved.id);
      setDraft(normalizePlan(saved));
      setSelectedRoomId(saved.rooms[0]?.id ?? null);
      setBanner(tr('Floor plan saved.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to save floor plan.'));
    } finally {
      setSaving(false);
    }
  }

  async function deletePlan() {
    if (!draft?.id) {
      setDraft(null);
      setSelectedPlanId(null);
      setSelectedRoomId(null);
      return;
    }
    if (!window.confirm(tr('Delete this floor plan and all mapped rooms?'))) return;
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/resources/floor-plans/${draft.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(tr('Unable to delete floor plan.'));
      }
      const remainingPlans = plans.filter((plan) => plan.id !== draft.id);
      setPlans(remainingPlans);
      if (remainingPlans.length) {
        selectPlan(remainingPlans[0]);
      } else {
        setDraft(null);
        setSelectedPlanId(null);
        setSelectedRoomId(null);
      }
      setBanner(tr('Floor plan deleted.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to delete floor plan.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="rounded-full p-2 hover:bg-slate-100" type="button">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">{tr('Operations')}</p>
            <h1 className="text-2xl font-semibold text-ink">{tr('Room Management')}</h1>
            <p className="text-sm text-slate-500">
              {tr('Upload floor plans, draw shared rooms, and map them to the schedulable room resources used by booking workflows.')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={startNewPlan}
          className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft"
        >
          <Plus size={16} />
          {tr('Add floor plan')}
        </button>
      </div>

      {banner ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{banner}</div> : null}
      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {tr('Loading room management settings...')}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <Map size={18} />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-ink">{tr('Floor plans')}</h2>
                  <p className="text-xs text-slate-500">
                    {tr('SVG or PNG works best for clean scaling, but JPG and WebP are supported too.')}
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {plans.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                    {tr('No floor plans yet. Start by uploading one and drawing your shared rooms over it.')}
                  </div>
                ) : (
                  plans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => selectPlan(plan)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                        selectedPlanId === plan.id ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-ink">{plan.floorLabel}</div>
                          <div className="text-xs text-slate-500">
                            {plan.name} • {plan.floorCode}
                          </div>
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            plan.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {plan.active ? tr('Active') : tr('Hidden')}
                        </span>
                      </div>
                      <div className="mt-3 text-xs text-slate-500">
                        {plan.rooms.length} {plan.rooms.length === 1 ? tr('mapped room') : tr('mapped rooms')}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-ink">{tr('Available room resources')}</h2>
              <p className="mt-1 text-xs text-slate-500">{tr('These are the shared room resources that can be placed on floor plans.')}</p>
              <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto">
                {roomResources.map((resource) => (
                  <div key={resource.id} className="rounded-2xl border border-slate-200 px-3 py-2">
                    <div className="text-sm font-medium text-ink">{resource.name}</div>
                    <div className="text-xs text-slate-500">
                      {resource.type}
                      {resource.locationId ? ` • ${resource.locationId}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </aside>

          <section className="space-y-4">
            {!draft ? (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-sm">
                {tr('Select a floor plan or create a new one to start mapping rooms.')}
              </div>
            ) : (
              <>
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Floor Editor')}</p>
                      <h2 className="mt-2 text-2xl font-semibold text-ink">{selectedPlanTitle}</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {tr('Draw rectangles over the floor plan, then bind each one to an existing room resource.')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                        disabled={uploading}
                      >
                        {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImageUp size={15} />}
                        {uploading ? tr('Uploading...') : tr('Upload plan')}
                      </button>
                      <button
                        type="button"
                        onClick={savePlan}
                        className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
                        disabled={saving || uploading}
                      >
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                        {tr('Save floor')}
                      </button>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
                    onChange={handlePlanUpload}
                  />

                  <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <Field
                      label={tr('Plan name')}
                      value={draft.name}
                      placeholder={tr('Main clinic level')}
                      onChange={(value) => updateDraft({ name: value })}
                    />
                    <Field
                      label={tr('Floor code')}
                      value={draft.floorCode}
                      placeholder={tr('floor-1')}
                      onChange={(value) => updateDraft({ floorCode: value })}
                    />
                    <Field
                      label={tr('Floor label')}
                      value={draft.floorLabel}
                      placeholder={tr('Floor 1')}
                      onChange={(value) => updateDraft({ floorLabel: value })}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
                    <label className="block text-sm">
                      <span className="mb-1.5 block text-sm font-medium text-slate-700">{tr('Team notes')}</span>
                      <textarea
                        value={draft.notes}
                        onChange={(event) => updateDraft({ notes: event.target.value })}
                        rows={4}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-300"
                        placeholder={tr('Optional setup notes for staff, wayfinding cues, or cleaning instructions.')}
                      />
                    </label>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Plan Status')}</div>
                      <label className="mt-4 flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-ink"
                          checked={draft.active}
                          onChange={(event) => updateDraft({ active: event.target.checked })}
                        />
                        <div>
                          <div className="text-sm font-semibold text-ink">{tr('Visible in visual room management')}</div>
                          <div className="text-xs text-slate-500">
                            {tr('Hidden floors stay in settings but do not appear on the live room board.')}
                          </div>
                        </div>
                      </label>
                      {draft.imageUrl ? (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-xs text-slate-500">
                          {draft.imageWidth && draft.imageHeight
                            ? `${draft.imageWidth} × ${draft.imageHeight}px`
                            : tr('Image uploaded')}
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-4 text-xs text-slate-500">
                          {tr('Upload an image before drawing rooms.')}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-ink">{tr('Draw shared rooms')}</h3>
                      <p className="text-sm text-slate-500">
                        {tr('Use rectangles for now. Draw on the plan, then fine-tune the geometry in the room panel.')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setDrawMode((current) => !current)}
                        className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
                          drawMode ? 'bg-emerald-100 text-emerald-800' : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                        }`}
                        disabled={!draft.imageUrl}
                      >
                        <PencilRuler size={15} />
                        {drawMode ? tr('Drawing on') : tr('Draw room')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedRoomId(null)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                      >
                        <X size={15} />
                        {tr('Clear selection')}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-5 2xl:grid-cols-[minmax(0,1fr)_320px]">
                    <div
                      ref={canvasRef}
                      className={`relative overflow-hidden rounded-[28px] border border-slate-200 bg-slate-100 ${drawMode ? 'cursor-crosshair' : 'cursor-default'}`}
                      style={{ aspectRatio: draft.imageWidth && draft.imageHeight ? `${draft.imageWidth}/${draft.imageHeight}` : '16 / 10' }}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseLeave={() => drawing && handleCanvasMouseUp()}
                      onMouseUp={handleCanvasMouseUp}
                    >
                      {draft.imageUrl ? (
                        <img src={draft.imageUrl} alt={draft.floorLabel || draft.name || tr('Floor plan')} className="h-full w-full object-contain" />
                      ) : (
                        <div className="flex h-full min-h-[440px] items-center justify-center text-sm text-slate-500">
                          {tr('Upload a floor plan to begin drawing shared rooms.')}
                        </div>
                      )}

                      {draft.imageUrl ? (
                        <div className="absolute inset-0">
                          {draft.rooms.map((room) => {
                            const resource = room.resource || roomResources.find((item) => item.id === room.resourceId);
                            const selected = room.clientId === selectedRoomId;
                            return (
                              <button
                                key={room.clientId}
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedRoomId(room.clientId);
                                }}
                                className={`absolute overflow-hidden rounded-2xl border-2 text-left transition ${
                                  selected ? 'border-ink shadow-lg' : 'border-white/80 shadow-md'
                                } bg-emerald-100/85`}
                                style={{
                                  left: `${room.x * 100}%`,
                                  top: `${room.y * 100}%`,
                                  width: `${room.width * 100}%`,
                                  height: `${room.height * 100}%`,
                                  backgroundColor: room.color || undefined,
                                }}
                              >
                                <div className="flex h-full flex-col justify-between p-2">
                                  <div className="truncate text-xs font-semibold text-emerald-900">
                                    {room.label || resource?.name || tr('Unassigned room')}
                                  </div>
                                  <div className="truncate text-[11px] text-emerald-800/80">{resource?.type || tr('Map this room')}</div>
                                </div>
                              </button>
                            );
                          })}

                          {drawing ? (
                            <div
                              className="absolute rounded-2xl border-2 border-dashed border-ink bg-white/25"
                              style={{
                                left: `${Math.min(drawing.startX, drawing.currentX) * 100}%`,
                                top: `${Math.min(drawing.startY, drawing.currentY) * 100}%`,
                                width: `${Math.abs(drawing.currentX - drawing.startX) * 100}%`,
                                height: `${Math.abs(drawing.currentY - drawing.startY) * 100}%`,
                              }}
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Mapped Rooms')}</div>
                        <div className="mt-3 max-h-[220px] space-y-2 overflow-y-auto">
                          {draft.rooms.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-5 text-sm text-slate-500">
                              {tr('No drawn rooms yet. Turn on draw mode and drag over the floor plan.')}
                            </div>
                          ) : (
                            draft.rooms.map((room) => {
                              const resource = room.resource || roomResources.find((item) => item.id === room.resourceId);
                              const selected = room.clientId === selectedRoomId;
                              return (
                                <button
                                  key={room.clientId}
                                  type="button"
                                  onClick={() => setSelectedRoomId(room.clientId)}
                                  className={`w-full rounded-2xl border px-3 py-2 text-left ${
                                    selected ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'
                                  }`}
                                >
                                  <div className="text-sm font-semibold text-ink">{room.label || resource?.name || tr('Untitled room')}</div>
                                  <div className="text-xs text-slate-500">{resource?.name || tr('Choose a room resource')}</div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {selectedRoom ? (
                        <div className="rounded-3xl border border-slate-200 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Selected Room')}</div>
                              <div className="mt-1 text-lg font-semibold text-ink">{selectedRoom.label || tr('Room shape')}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => deleteRoom(selectedRoom.clientId)}
                              className="rounded-full p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>

                          <div className="mt-4 space-y-3">
                            <label className="block text-sm">
                              <span className="mb-1.5 block font-medium text-slate-700">{tr('Link resource')}</span>
                              <select
                                value={selectedRoom.resourceId}
                                onChange={(event) => {
                                  const resource = roomResources.find((item) => item.id === event.target.value);
                                  updateRoom(selectedRoom.clientId, {
                                    resourceId: event.target.value,
                                    resource,
                                    label: selectedRoom.label || resource?.name || '',
                                  });
                                }}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-300"
                              >
                                <option value="">{tr('Choose a room resource')}</option>
                                {roomResources.map((resource) => (
                                  <option key={resource.id} value={resource.id}>
                                    {resource.name} ({resource.type})
                                  </option>
                                ))}
                              </select>
                            </label>

                            <Field
                              label={tr('Display label')}
                              value={selectedRoom.label}
                              placeholder={tr('Exam room 2')}
                              onChange={(value) => updateRoom(selectedRoom.clientId, { label: value })}
                            />

                            <div className="grid grid-cols-2 gap-3">
                              <PercentField
                                label={tr('Left')}
                                value={selectedRoom.x}
                                onChange={(value) =>
                                  updateRoom(selectedRoom.clientId, { x: clamp(value, 0, Math.max(0, 1 - selectedRoom.width)) })
                                }
                              />
                              <PercentField
                                label={tr('Top')}
                                value={selectedRoom.y}
                                onChange={(value) =>
                                  updateRoom(selectedRoom.clientId, { y: clamp(value, 0, Math.max(0, 1 - selectedRoom.height)) })
                                }
                              />
                              <PercentField
                                label={tr('Width')}
                                value={selectedRoom.width}
                                onChange={(value) =>
                                  updateRoom(
                                    selectedRoom.clientId,
                                    { width: clamp(value, 0.01, Math.max(0.01, 1 - selectedRoom.x)) },
                                  )
                                }
                              />
                              <PercentField
                                label={tr('Height')}
                                value={selectedRoom.height}
                                onChange={(value) =>
                                  updateRoom(
                                    selectedRoom.clientId,
                                    { height: clamp(value, 0.01, Math.max(0.01, 1 - selectedRoom.y)) },
                                  )
                                }
                              />
                            </div>

                            <Field
                              label={tr('Overlay color')}
                              value={selectedRoom.color}
                              placeholder="rgba(187, 247, 208, 0.85)"
                              onChange={(value) => updateRoom(selectedRoom.clientId, { color: value })}
                            />

                            <label className="block text-sm">
                              <span className="mb-1.5 block font-medium text-slate-700">{tr('Room notes')}</span>
                              <textarea
                                value={selectedRoom.notes}
                                onChange={(event) => updateRoom(selectedRoom.clientId, { notes: event.target.value })}
                                rows={3}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-300"
                                placeholder={tr('Optional operations notes for this shared room.')}
                              />
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
                          {tr('Select a mapped room to edit its resource link, label, or geometry.')}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={deletePlan}
                    className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 size={15} />
                    {draft.id ? tr('Delete floor') : tr('Discard draft')}
                  </button>
                  <button
                    type="button"
                    onClick={savePlan}
                    className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
                    disabled={saving || uploading}
                  >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    {tr('Save floor')}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-slate-700">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-300"
      />
    </label>
  );
}

function PercentField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-slate-700">{label}</span>
      <input
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={(value * 100).toFixed(1)}
        onChange={(event) => onChange(Number(event.target.value || 0) / 100)}
        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-300"
      />
    </label>
  );
}
